// «Рынок → Экспорт»: таможня импортёров (CIF). Сервер отдаёт коды стран и
// позиций; имена — Intl и каталог. Таблица: строка — позиция × импортёр ×
// партнёр, колонки — годы (Σ стоимость / Σ масса) и последние месяцы.

import type { CustomsSeries } from '../api'
import type { Lang } from '../i18n'

/** ISO3 стран, которые отдаёт таможня, → ISO2 для Intl. ЕС — «EU». */
const ISO2: Record<string, string> = {
  EUU: 'EU', JPN: 'JP', NOR: 'NO', RUS: 'RU', AUT: 'AT', BEL: 'BE', BGR: 'BG', CYP: 'CY',
  CZE: 'CZ', DEU: 'DE', DNK: 'DK', EST: 'EE', ESP: 'ES', FIN: 'FI', FRA: 'FR', GRC: 'GR',
  HRV: 'HR', HUN: 'HU', IRL: 'IE', ITA: 'IT', LTU: 'LT', LUX: 'LU', LVA: 'LV', MLT: 'MT',
  NLD: 'NL', POL: 'PL', PRT: 'PT', ROU: 'RO', SWE: 'SE', SVN: 'SI', SVK: 'SK',
}

export function countryName(iso3: string, lang: Lang): string {
  const iso2 = ISO2[iso3]
  if (!iso2) return iso3
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(iso2) ?? iso3
  } catch {
    return iso3 // старый браузер — код как есть
  }
}

export interface Row {
  key: string
  item_key: string
  processing: string
  thermal: string
  size_grade: string | null
  country: string
  city: string
  partner: string
  years: Record<number, { usd: number | null; rub: number | null; tonnes: number | null; months: number | null; annual: boolean }>
  months: Record<string, { usd: number | null; rub: number | null; tonnes: number | null }>
}

/** Последние `n` месяцев, по которым есть хоть одна точка, по возрастанию. */
export function lastMonths(data: CustomsSeries, n: number): string[] {
  return [...new Set(data.months.map((m) => m.month))].sort().slice(-n)
}

export function yearsOf(data: CustomsSeries): number[] {
  return [...new Set(data.years.map((y) => y.year))].sort()
}

/** Строки таблицы: ЕС и Россия-партнёр — первыми, внутри — по позиции. */
export function buildRows(data: CustomsSeries): Row[] {
  const proc = new Map(data.items.map((i) => [i.item_key, i]))
  const rows = new Map<string, Row>()
  const row = (c: { item_key: string; country: string; city?: string; partner: string }) => {
    const city = c.city ?? ''
    const key = `${c.item_key}|${c.country}|${city}|${c.partner}`
    let r = rows.get(key)
    if (!r) {
      const it = proc.get(c.item_key)
      r = { key, item_key: c.item_key, processing: it?.processing ?? '', thermal: it?.thermal ?? '',
        size_grade: it?.size_grade ?? null, country: c.country, city,
        partner: c.partner, years: {}, months: {} }
      rows.set(key, r)
    }
    return r
  }
  for (const y of data.years) {
    row(y).years[y.year] = { usd: y.usd_per_kg, rub: y.rub_per_kg, tonnes: y.tonnes, months: y.months, annual: y.annual }
  }
  for (const m of data.months) {
    row(m).months[m.month] = { usd: m.usd_per_kg, rub: m.rub_per_kg, tonnes: m.tonnes }
  }
  const order = (r: Row) => [r.partner === 'RUS' ? 0 : 1, r.country === 'EUU' ? 0 : 1]
  return [...rows.values()].sort((a, b) => {
    const [pa, ca] = order(a)
    const [pb, cb] = order(b)
    return pa - pb || a.item_key.localeCompare(b.item_key) || ca - cb
      || a.country.localeCompare(b.country) || a.city.localeCompare(b.city)
  })
}

/** Подсказка ячейки: тонны, если масса известна; иначе — что цена простым средним. */
export function cellTipKey(kind: 'year' | 'yearAnnual' | 'month', tonnes: number | null): string {
  const base = { year: 'yearTip', yearAnnual: 'yearTipAnnual', month: 'monthTip' }[kind]
  return `prices.export.${base}${tonnes === null ? 'NoVolume' : ''}`
}

/** Ключи подписи продукции: разделка, а термосостояние — только если не «мороженая»
 *  (живого и мороженого краба иначе не различить). */
export function productKeys(r: { processing: string; thermal: string }): string[] {
  const keys = [`prices.processing.${r.processing}`]
  if (r.thermal && r.thermal !== 'frozen') keys.push(`prices.thermal.${r.thermal}`)
  return keys
}

/** Вкладка: «Экспорт» — таможня, «Опт» — первая продажа за рубежом,
 *  «Опт России» — «Рыбные ряды» НФР (рубли, без партнёра). */
export type Market = 'export' | 'wholesale' | 'wholesale_ru'

/** Строка покрытия: годовой источник — год, недельный — неделя, дневной — последний день. */
export function coverageLineKey(period: string | null): string {
  return {
    year: 'prices.export.coverageLineYear', week: 'prices.export.coverageLineWeek',
    day: 'prices.export.coverageLineDay',
  }[period ?? ''] ?? 'prices.export.coverageLine'
}

/** Валюта ячейки: опт России называют в рублях, остальное — в долларах. */
export function marketCurrency(market: Market): 'usd' | 'rub' {
  return market === 'wholesale_ru' ? 'rub' : 'usd'
}

const MARKET_KEYS = new Set(['importer', 'from', 'allPartners', 'unit', 'empty', 'coverageNote'])

/** Ключ подписи: то, что у вкладок разное, берётся из каталога вкладки. */
export function marketKey(market: Market, k: string): string {
  return `prices.${MARKET_KEYS.has(k) ? market : 'export'}.${k}`
}
